import { gql } from '@apollo/client';
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconChevronRight from '@apollo/icons/default/IconChevronRight.svg';
import IconOperations from '@apollo/icons/default/IconOperations.svg';
import IconProposals from '@apollo/icons/default/IconProposals.svg';
import IconSettings from '@apollo/icons/default/IconSettings.svg';
import {
  Avatar,
  ButtonGroup,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Group,
  IconButton,
} from '@apollo/orbit';
import classnames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Link as ReactRouterLink } from 'react-router-dom';

import * as orgRoutes from 'src/app/account/routes';
import {
  graphPersistedQueriesRouteConfig,
  graphProposalListRouteConfig,
  graphSettingsRouteConfig,
} from 'src/app/graph/routes';
import { Icon as IconPersistedQueries } from 'src/assets/icon-keylock-sl.svg';
import { HiddenBadge } from 'src/components/common/hiddenBadge/HiddenBadge';
import { MarbledAvatar } from 'src/components/common/MarbledAvatar';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { match } from 'src/components/common/typeahead/fuzzy';
import { useCapabilities } from 'src/hooks/useCapabilities';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { useLDFlag } from 'src/lib/launchDarkly';
import { scrollToIfNotInView } from 'src/lib/scrollTo';

import { SupergraphsVariantsTable } from './SupergraphsVariantsTable';

const SupergraphListGraphFirstLoadMetadataFragment = gql`
  fragment SupergraphListGraphFirstLoadMetadataFragment on GraphVariant {
    id
    name
    url
    isFavoriteOfCurrentUser
    isProtected
    isContract
    isPublic
    hasManagedSubgraphs
    hasSupergraphSchema
    latestLaunch {
      id
      build {
        id
        result {
          ... on BuildFailure {
            errorMessages {
              message
            }
          }
        }
      }
    }
    activeSchemaPublish {
      id
      publishedAt
      schema {
        hash
        createdAt
        typeCount
        fieldCount
      }
    }
  }
`;

export const SupergraphsListItemGraphFragment = gql`
  fragment SupergraphsListItemGraphFragment on Service {
    id
    graphType
    title
    avatarUrl
    description
    hiddenFromUninvitedNonAdminAccountMembers
    roles {
      canQueryTokens
      canUpdateTitle
      canUpdateAvatar
      canQueryPersistedQueryLists
      canQueryProposals
      canQueryStats
      canQueryImplementingServices
    }
    variants {
      id
      ...SupergraphListGraphFirstLoadMetadataFragment
    }
  }
  ${SupergraphListGraphFirstLoadMetadataFragment}
`;

export const SupergraphsListItem = ({
  searchText,
  isInFavSection = false,
  graph,
}: {
  searchText?: string;
  isInFavSection?: boolean;
  graph: GraphQLTypes.SupergraphsListItemGraphFragment;
}) => {
  const {
    id: graphId,
    title,
    avatarUrl,
    description,
    variants,
    hiddenFromUninvitedNonAdminAccountMembers,
    roles,
  } = graph;
  const [inViewRef, inView] = useInView();
  const [expanded, setExpanded] = useState(true);
  const [highlightFinished, setHighlightFinished] = useState(false);

  const { highlightSupergraphState } = useRouteParams(orgRoutes.graphs);
  const shouldHighlight =
    highlightSupergraphState?.graphId === graphId && !highlightFinished;

  const favs = variants.filter(
    ({ isFavoriteOfCurrentUser }) => isFavoriteOfCurrentUser,
  );

  const graphHasFederatedVariants = useMemo(
    () =>
      variants.some(
        (variant) => variant.hasManagedSubgraphs || variant.hasSupergraphSchema,
      ),
    [variants],
  );

  const shouldShowNewPersistedQueriesIcon = useLDFlag(
    'gov-pq-ux-sort-filter-delete',
  );
  const canAccessProposals = useCapabilities().capabilities?.proposals;

  useEffect(() => {
    // remove highlight state after 5s after data has loaded
    // don't do timing based visual changes on chromatic
    if (highlightSupergraphState?.graphId === graphId) {
      scrollToIfNotInView(`#${graphId}`);
      const timeout = setTimeout(() => {
        setHighlightFinished(true);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [graphId, highlightSupergraphState?.graphId, shouldHighlight]);

  return (
    <Card
      id={graphId}
      className={classnames(
        'mb-4 transition-background duration-500',
        shouldHighlight && 'bg-highlight',
      )}
      ref={inViewRef}
    >
      <CardHeader>
        <Group gap={4}>
          {!isInFavSection &&
            (avatarUrl ? (
              <Avatar src={avatarUrl} variant="graph" />
            ) : (
              <MarbledAvatar
                animate={inView}
                identifier={graphId}
                className="flex-none"
              />
            ))}
          <Group orientation="vertical">
            <CardTitle className="flex flex-1 items-center">
              {searchText ? (
                <span>
                  {
                    match(searchText, title, {
                      elem: (
                        <span className="bg-searchhighlight text-inverted" />
                      ),
                    })?.rendered
                  }
                </span>
              ) : (
                title
              )}
              {hiddenFromUninvitedNonAdminAccountMembers ? (
                <HiddenBadge />
              ) : null}
            </CardTitle>
            {(isInFavSection || description) && (
              <CardDescription>
                {isInFavSection
                  ? `${favs.length} favorited ${format.pluralize(
                      favs.length,
                      'variant',
                    )}`
                  : description}
              </CardDescription>
            )}
          </Group>
        </Group>
        <ButtonGroup>
          {graphHasFederatedVariants &&
            canAccessProposals &&
            roles?.canQueryProposals && (
              <Tooltip placement="top" label="Go to schema change proposals">
                <IconButton
                  data-testid={`supergraph-list-proposals-button-${graphId}`}
                  aria-label="Go to schema change proposals"
                  variant="hidden"
                  size="sm"
                  icon={<IconProposals />}
                  as={ReactRouterLink}
                  to={graphProposalListRouteConfig.path({
                    graphId,
                    status: [
                      GraphQLTypes.ProposalStatus.OPEN,
                      GraphQLTypes.ProposalStatus.DRAFT,
                      GraphQLTypes.ProposalStatus.APPROVED,
                    ],
                  })}
                />
              </Tooltip>
            )}

          {variants[0]?.name && roles?.canQueryPersistedQueryLists && (
            <Tooltip placement="top" label="Go to persisted query lists">
              <IconButton
                data-testid={`supergraph-list-knownops-button-${graphId}`}
                aria-label="Go to persisted query lists"
                variant="hidden"
                size="sm"
                icon={
                  shouldShowNewPersistedQueriesIcon ? (
                    <IconPersistedQueries />
                  ) : (
                    <IconOperations />
                  )
                }
                as={ReactRouterLink}
                to={graphPersistedQueriesRouteConfig.path({ graphId })}
              />
            </Tooltip>
          )}

          {variants[0]?.name &&
            roles?.canQueryTokens &&
            roles?.canUpdateTitle &&
            roles?.canUpdateAvatar && (
              <Tooltip placement="top" label="Go to graph settings">
                <IconButton
                  data-testid={`supergraph-list-setting-button-${graphId}`}
                  aria-label="Go to graph settings"
                  variant="hidden"
                  size="sm"
                  icon={<IconSettings />}
                  as={ReactRouterLink}
                  to={graphSettingsRouteConfig.path({
                    graphId,
                    graphVariant: variants[0].name,
                  })}
                />
              </Tooltip>
            )}
          <Tooltip
            placement="top"
            label={expanded ? 'Collapse variants' : 'Expand variants'}
          >
            <IconButton
              onClick={() => setExpanded(!expanded)}
              aria-label={expanded ? 'Collapse variants' : 'Expand variants'}
              variant="hidden"
              size="sm"
              icon={expanded ? <IconChevronDown /> : <IconChevronRight />}
            />
          </Tooltip>
        </ButtonGroup>
      </CardHeader>
      {expanded && (
        <CardBody>
          <SupergraphsVariantsTable
            isInFavSection={isInFavSection}
            inView={inView}
            graph={graph}
          />
        </CardBody>
      )}
    </Card>
  );
};
